import { forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";

import { cn } from "../../index";
import CircleBase, { type CircleBaseProps } from "../base/CircleBase";
import { CircleContextProvider } from "./CircleContext";

export interface CircleProps extends CircleBaseProps {
  selected?: boolean;
  disabled?: boolean;
}

const CircleRoot = forwardRef<HTMLDivElement, CircleProps>(
  ({ selected = true, disabled = false, size: sizeProp = 40, children, onClick, className, style, ...props }, ref) => {
    const innerRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => {
      if (!innerRef.current) {
        throw new Error("CircleRoot ref accessed before mount");
      }
      return innerRef.current;
    });

    const [renderedSize, setRenderedSize] = useState<number | undefined>(undefined);

    const size = renderedSize ?? sizeProp;
    const borderRadius = typeof style?.borderRadius === "number" ? style.borderRadius : size / 2;

    useLayoutEffect(() => {
      if (!innerRef.current) return;
      const el = innerRef.current;
      if (el.offsetWidth === 0 || el.offsetHeight === 0) {
        setRenderedSize(undefined);
        return;
      }
      if (el.offsetWidth === el.offsetHeight) {
        setRenderedSize(el.offsetWidth !== sizeProp ? el.offsetWidth : undefined);
      }
    }, [sizeProp, style]);

    return (
      <CircleContextProvider size={size} borderRadius={borderRadius}>
        <CircleBase
          {...props}
          ref={innerRef}
          size={size}
          style={style}
          className={cn(
            `relative shrink-0 opacity-50 data-[disabled=true]:bg-primary-dark-gray data-[disabled=true]:text-primary-white data-[disabled=true]:opacity-100 data-[selected=true]:opacity-100`,
            className,
          )}
          onClick={e => !disabled && onClick?.(e)}
          data-selected={selected}
          data-disabled={disabled}
        >
          {children}
        </CircleBase>
      </CircleContextProvider>
    );
  },
);

CircleRoot.displayName = "Circle.Root";

export default CircleRoot;
